import { z } from 'zod'

import { validatePhoneNumber } from '@/lib/utils'

// Combined login schema (email/password required; code optional)
export const guestLoginWithOtpSchema = z.object({
  email: z
    .email('Please enter a valid email address')
    .trim()
    .min(1, 'Username or email is required'),
  password: z.string(),
  code: z
    .string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d+$/, 'Code must be numeric')
    .or(z.literal(''))
    .optional(),
})
export type GuestLoginWithOtpSchema = z.infer<typeof guestLoginWithOtpSchema>

// Create account schema
export const createAccountSchema = z.object({
  name: z
    .string()
    .trim()
    .min(2, 'Account name must be at least 2 characters')
    .max(50, 'Account name cannot exceed 50 characters'),
  firstName: z.string().trim().optional(),
  lastName: z.string().trim().optional(),
  phone: z
    .union([z.string().trim(), z.literal('')])
    .refine(
      (val) => validatePhoneNumber(val, { allowEmpty: true }),
      'Invalid phone number. Select a country code and enter a valid number.'
    )
    .optional(),
  email: z
    .email('Please enter a valid email address')
    .trim()
    .min(1, 'Username or email is required'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .max(128, 'Password cannot exceed 128 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
  code: z
    .string()
    .length(6, 'Code must be 6 digits')
    .regex(/^\d+$/, 'Code must be numeric')
    .or(z.literal(''))
    .optional(),
  'terms&Conditions': z.boolean().refine((val) => val, {
    message: 'You must accept the terms and conditions',
  }),
})
export type CreateAccountSchema = z.infer<typeof createAccountSchema>

export const passwordRecoverySentSchema = z.object({
  email: z
    .email('Please enter a valid email address')
    .trim()
    .min(1, 'Username or email is required'),
})
export type PasswordRecoverySentSchema = z.infer<typeof passwordRecoverySentSchema>

export const passwordRecoverySchema = z
  .object({
    password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .max(128, 'Password cannot exceed 128 characters')
      .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
      .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
      .regex(/[0-9]/, 'Password must contain at least one number')
      .regex(/[^A-Za-z0-9]/, 'Password must contain at least one special character'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ['confirmPassword'],
  })
export type PasswordRecoverySchema = z.infer<typeof passwordRecoverySchema>
